import { defineStore } from "pinia";
import { ref, computed } from "vue";
import api from "../services/api";
import { useSocketStore } from "./socket";
import { useDeviceStore } from "./device";

export const useVotesStore = defineStore("votes", () => {
  // State
  const votes = ref({});
  const userVotedSongs = ref({}); // Track which songs the user (device) has voted for
  const upNextSongIds = ref({});
  const queueEntries = ref({});
  const nowPlaying = ref({});
  const isLoading = ref(false);
  const currentEventId = ref(null);

  // Getters
  const getEventVotes = computed(() => (eventId) => {
    return votes.value[eventId] || [];
  });

  const getSongVoteCount = computed(() => (eventId, songId) => {
    const eventVotes = votes.value[eventId] || [];
    const songVote = eventVotes.find((vote) => vote.songId === songId);
    return songVote ? songVote.totalVotes : 0;
  });

  const hasUserVotedForSong = computed(() => (eventId, songId) => {
    const eventVotedSongs = userVotedSongs.value[eventId] || [];
    return eventVotedSongs.includes(songId);
  });

  const getUpNextSongId = computed(() => (eventId) => {
    return upNextSongIds.value[eventId] || null;
  });

  const getQueueEntries = computed(() => (eventId) => {
    return queueEntries.value[eventId] || [];
  });

  const getNowPlaying = computed(() => (eventId) => {
    return nowPlaying.value[eventId] || null;
  });

  const applyEventVotePayload = (eventId, data) => {
    if (Array.isArray(data.queueEntries)) {
      queueEntries.value[eventId] = data.queueEntries;
      nowPlaying.value[eventId] = data.nowPlaying || null;
      votes.value[eventId] = [];
      upNextSongIds.value[eventId] = null;
      return;
    }

    votes.value[eventId] = data.results || [];
    upNextSongIds.value[eventId] = data.upNextSongId || null;
    queueEntries.value[eventId] = [];
    nowPlaying.value[eventId] = null;
  };

  // Actions
  const submitVote = async (eventId, songId) => {
    try {
      isLoading.value = true;

      const deviceStore = useDeviceStore();
      const deviceId = await deviceStore.getDeviceId();

      const response = await api.post("/api/votes", {
        eventId,
        songId,
        deviceId // Include deviceId for anonymous voting
      });

      if (response.data.success) {
        // Update local state
        await fetchEventVotes(eventId);

        // Track that user has voted for this song
        if (!userVotedSongs.value[eventId]) {
          userVotedSongs.value[eventId] = [];
        }
        if (!userVotedSongs.value[eventId].includes(songId)) {
          userVotedSongs.value[eventId].push(songId);
        }

        // Emit socket event
        const socketStore = useSocketStore();
        socketStore.emit("submit_vote", { eventId, songId });

        return { success: true };
      }
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || "Failed to submit vote"
      };
    } finally {
      isLoading.value = false;
    }
  };

  const fetchEventVotes = async (eventId) => {
    try {
      const response = await api.get(`/api/votes/event/${eventId}`);

      if (response.data.success) {
        applyEventVotePayload(eventId, response.data.data);
        currentEventId.value = eventId;
      }
    } catch (error) {
      console.error("Failed to fetch event votes:", error);
    }
  };

  const fetchUserVotes = async (eventId) => {
    try {
      const deviceStore = useDeviceStore();
      const deviceId = await deviceStore.getDeviceId();

      const response = await api.get(`/api/votes/device/${eventId}`, {
        params: { deviceId }
      });

      if (response.data.success) {
        userVotedSongs.value[eventId] = response.data.data.votes || [];
      }
    } catch (error) {
      console.error("Failed to fetch user votes:", error);
    }
  };

  // Socket event handlers
  const handleVoteUpdated = async (data) => {
    try {
      const { eventId, results } = data;

      if (!eventId) {
        return;
      }

      // If the socket event includes pre-computed results, use them directly
      // This avoids an extra API call and ensures consistency with backend sorting
      if (results && Array.isArray(results)) {
        votes.value[eventId] = results;
        console.log(`✅ Vote results updated from socket for event ${eventId}`);
      } else {
        // Fallback: refetch votes to ensure we have the latest data
        // This ensures accurate vote counts even if the structure changes
        await fetchEventVotes(eventId);
      }
    } catch (error) {
      console.error("Error handling vote update:", error);
    }
  };

  const handleSongPerformed = async (data) => {
    const { eventId, upNextSongId } = data;

    // Update upNextSongId immediately from socket event
    if (eventId && upNextSongId !== undefined) {
      upNextSongIds.value[eventId] = upNextSongId || null;
    }

    // Refetch votes to get the latest performedAt timestamp and ensure accurate data
    await fetchEventVotes(eventId);
  };

  const handleSongEnded = async (data) => {
    const { eventId } = data;

    // Refetch votes to get the latest endedAt timestamp and ensure accurate data
    await fetchEventVotes(eventId);
  };

  const clearVotes = () => {
    votes.value = {};
    currentEventId.value = null;
  };

  return {
    votes,
    userVotedSongs,
    upNextSongIds,
    queueEntries,
    nowPlaying,
    isLoading,
    currentEventId,
    getEventVotes,
    getQueueEntries,
    getNowPlaying,
    getSongVoteCount,
    hasUserVotedForSong,
    getUpNextSongId,
    submitVote,
    fetchEventVotes,
    fetchUserVotes,
    handleVoteUpdated,
    handleSongPerformed,
    handleSongEnded,
    clearVotes
  };
});
